import React, { useState, useEffect, useRef } from "react";
import { Button, Form, DropdownButton, Dropdown, Col, Row } from "react-bootstrap";
import { SupplierUserPopUp } from '../components/SupplierUserPopUp';
import { Loading } from "./Loading";
import { TableFilter } from '../components/TableFilter';
import ServiceCalls from "../utils/serviceCalls.js";
import CommonUtils from "../utils/siteUtils";
import * as XLSX from "@e965/xlsx/xlsx.mjs";
import "../styles/Admin.css";
import "../styles/PopUps.css";
import DataTable from "react-data-table-component";

export const AdminInactiveAccounts = (props) => {
	
	const [isLoading, setIsLoading] = useState(false);
	const [filterText, setFilterText] = React.useState('');
	const [tableData, setTableData] = React.useState([]);
	const [statusFilter, setStatusFilter] = React.useState('All');
	const [totalRows, setTotalRows] = React.useState(0);
	const [triggerClear, setTriggerClear] = useState(false); // Flag to trigger search after clearing filters
	const [sortedData, setSortedData] = React.useState([]); // Track sorted data for export
	const isMounted = useRef(true);

	//Get All Inactive Accounts on mount
	useEffect(() => {
		const fetchData = async () => {
			await getInactiveAccounts(true); // true = get all on initial load
		};

		fetchData();

		return () => {
			isMounted.current = false;
		};
	}, []);

	// Trigger search when filters are cleared
	useEffect(() => {
		if (triggerClear) {
			getInactiveAccounts();
			setTriggerClear(false);
		}
	}, [filterText, statusFilter, triggerClear]);

	const handleSearch = async () => {
		await getInactiveAccounts(false);
	}

	const handleClear = () => {
		setFilterText('');
		setStatusFilter('All');
		// Set a flag to trigger search
		setTriggerClear(true);
	}


	const getInactiveAccounts = async () => {

		setIsLoading(true);
		//Check if page access is still allowed
		props.onChildUpdate();

		// Server handles all filtering
		var searchInfo = {
			searchText: filterText || "",
			statusFilter: statusFilter || "All"
		};
		console.log("Calling getAdminInactiveAccounts with searchInfo:", searchInfo);

		// Build searchInfo object with current filter values for server-side filtering
		ServiceCalls.getAdminInactiveAccounts(searchInfo)
			.then(function (data) {
				console.log("Raw data received from getAdminInactiveAccounts:", data);
				if (data && isMounted.current) {
					const preparedData = prepData(data);
					console.log("Prepared data for table:", preparedData);
					// DE1312049 Apply default sort (Update Date descending) to match UI display
					const defaultSorted = [...preparedData].sort((rowA, rowB) => {
						const dateA = rowA.updateDateRaw ? rowA.updateDateRaw.getTime() : 0;
						const dateB = rowB.updateDateRaw ? rowB.updateDateRaw.getTime() : 0;
						return dateB - dateA;
					});

					setTableData(preparedData);
					setSortedData(defaultSorted); // Initialize sorted data
					setTotalRows(preparedData.length);
				}
			})
			.catch((error) => {
				console.error("Error occurred while fetching inactive results:", error);
			})
			.finally(() => {
				if (isMounted.current) {
					setIsLoading(false);
				}
			});
	}; 

	const prepData = (disabledUsers) => {
		const tempArr = [];
		if (disabledUsers) {
			for (let i = 0; i < disabledUsers.length; i++) {
				var contact = disabledUsers[i];
				for (let z = 0; z < contact.suppliers.length; z++) {
					
					var supplier = contact.suppliers[z];

					// Get the most recent updated date from roles
					const updatedDates = supplier.contactRoles.map(r => r.updatedDt).filter(d => d != null).map(d => new Date(d));

					const updateDateRaw = updatedDates.length > 0? new Date(Math.max(...updatedDates)): null;		
				var newUser = {
					lastName: (contact.lastName === '' || contact.lastName == null ? "--" : contact.lastName),
					firstName: (contact.firstName === '' || contact.firstName == null ? "--" : contact.firstName),
					emailAddress: contact.email,
					lastSignOnDate: CommonUtils.renderStatusDate(contact.lastSignOnDate),
					status: CommonUtils.statusNameForContact(contact),
					statusDate: CommonUtils.renderStatusDate(contact.statusDate),
					contactId: contact.contactId,
					registration: contact.ohcidRegistration,
					statusNotes: '',
					active: contact.profileStatus,				
					supplierId: supplier.supplierId,
					supplierName: supplier.supplierName,
					roles: supplier.contactRoles,
					updateDateRaw: updateDateRaw,
				};
					tempArr.push(newUser);
				}
			}
		}
		return tempArr
	}
	const prepForExport = () => {
		// Use sortedData which reflects the current UI sort state
		var exportData = sortedData.map((log) => ({
			"Last Name": log.lastName,
			"First Name": log.firstName,
			"Contact Email": log.emailAddress,
			"Supplier Name": log.supplierName,
			"Last Signed On": log.lastSignOnDate,
			"Update Date": log.updateDateRaw ? CommonUtils.renderStatusDate(log.updateDateRaw.toISOString()) : '--',
			"Status": CommonUtils.statusNameForContact(log),
		}));

		return exportData;
	};

	const exportExcelResults = () => {
		const ws = XLSX.utils.json_to_sheet(prepForExport());
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, "PSSP_InActive Accounts");
		XLSX.writeFile(wb, "PSSP_Restricted_Users.xlsx");
	};

	const exportCSVResults = () => {
		const ws = XLSX.utils.json_to_sheet(prepForExport());
		const csv = XLSX.utils.sheet_to_csv(ws);
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = "PSSP_Restricted_Users.csv";
		link.click();
	};

	const handleSort = (column, sortDirection) => {
		// This callback is triggered when the user sorts the table
		const sorted = [...tableData].sort((rowA, rowB) => {
			// DE1312049 Apply the appropriate sort function based on the column
			let comparison = 0;

			if (column.sortFunction) {
				comparison = column.sortFunction(rowA, rowB);
			} else if (column.selector) {
				const aValue = typeof column.selector === 'function' ? column.selector(rowA) : rowA[column.selector];
				const bValue = typeof column.selector === 'function' ? column.selector(rowB) : rowB[column.selector];

				if (aValue < bValue) comparison = -1;
				else if (aValue > bValue) comparison = 1;
			}

			return sortDirection === 'asc' ? comparison : -comparison;
		});

		setSortedData(sorted);
	};
		
	return (
		<div className="auditLogsContainer">
			<div id="auditLogsArea" className="flex-column borderCurve" style={{ marginTop: "10px" }}>
				<div className="auditLogsHeader">
					<h3>Restricted Users</h3>
				</div>

				<Form
					onSubmit={(e) => {
						e.preventDefault();
						handleSearch();
					}}>{/*US10171254 Updated UI for filtering inactive accounts */ }
					<Row className="align-items-end">
						<Col md={2}>
							<Form.Label className="formLabel">Status</Form.Label>
							<Form.Select
								value={statusFilter}
								onChange={(e) => setStatusFilter(e.target.value)}
							>
								<option value="All">All</option>
								<option value="Disabled">Disabled</option>
								<option value="Locked">Locked</option>
							</Form.Select>
						</Col>
						<Col md={8} style={{ marginBottom: "-5px" }}>
							<TableFilter
								onFilter={(text) => setFilterText(text)}
								onFilterClear={() => {
									setFilterText('');
									setTriggerClear(true);
								}}
								label="Search on Contact details or supplier name"
								filterText={filterText}
								key={filterText}
							/>
						</Col>
						<Col md style={{ paddingLeft: "unset" }}>
							<div className="d-flex justify-content-end">
								<Button
									variant="secondary"
									className="mr-2 align-top auditLogsSearchButton"
									onClick={handleClear}
									style={{ marginRight: "5px" }}
								>
									Clear
								</Button>
								<Button
									variant="primary"
									type="submit"
									className="mr-2 align-top auditLogsSearchButton"
								>
									Search
								</Button>
							</div>
						</Col>
					</Row>
				</Form>
			</div>

			{!isLoading && totalRows > 0 && (
				<div className="d-flex" style={{ float: "right", marginTop: "15px" }}>
					<div className="searchResultsCnt">
						<span className="searchResultsPill">{totalRows}</span> Results
					</div>
					<DropdownButton
						title="Export Results"
						className="auditTypeExport align-top"
						variant="primary"
						style={{ marginTop: "24px", marginBottom: "2px", marginRight: "17px" }}
					>
						<Dropdown.Item eventKey="1" onClick={exportCSVResults}>
							CSV
						</Dropdown.Item>
						<Dropdown.Item eventKey="2" onClick={exportExcelResults}>
							Excel
						</Dropdown.Item>
					</DropdownButton>
				</div>
			)}
			{isLoading && <Loading />}
			{!isLoading && totalRows > 0 && (
				<div id="auditLogsArea" className="flex-column borderCurve" style={{ marginTop: "25px" }}>
					<InactiveAccountsTable data={tableData} currentUser={props.currentUser} onTableUpdate={getInactiveAccounts} onSort={handleSort} />
				</div>
			)}
			{!isLoading && totalRows === 0 && (
				<div style={{ marginTop: "10px", marginLeft: "10px" }}>No results found</div>
			)}
		</div>
	);
};

const InactiveAccountsTable = ({ data, currentUser, onTableUpdate, onSort }) => {


	const onUserUpdate = () => {
		onTableUpdate();
	};	
	//Table columns
	var columns = [
		{
			name: 'Last Name',
			selector: row => row.lastName,
			sortFunction: (rowA, rowB) => CommonUtils.customStringSort(rowA, rowB, (row) => row.lastName), //sorting ability for all columns
			maxWidth: "150px",
		},
		{
			name: 'First Name',
			selector: row => row.firstName,
			sortFunction: (rowA, rowB) => CommonUtils.customStringSort(rowA, rowB, (row) => row.firstName),
			maxWidth: "150px",
		},
		{
			name: 'Contact Email',
			selector: row => (
				<div className="access-breakWord">
					{row.emailAddress}
				</div>
			),
			sortFunction: (rowA, rowB) => CommonUtils.customStringSort(rowA, rowB, (row) => row.emailAddress),
			wrap: true,
			grow: 1.3
		},
		{
			name: 'Supplier Name',
			selector: row => row.supplierName,
			sortFunction: (rowA, rowB) => CommonUtils.customStringSort(rowA, rowB, (row) => row.supplierName),
			maxWidth: "250px",
		},
		{
			name: 'Last Signed On',
			selector: row => row.lastSignOnDate,
			sortable: true,
			maxWidth: "150px",
		},
		{
			id: 'updateDate', 
			name: 'Update Date', //new column - Update Date from supplierprofiles
			selector: row => row.updateDateRaw,
			sortable: true,
			format: row => {
				if (row.updateDateRaw) {
					return CommonUtils.renderStatusDate(row.updateDateRaw.toISOString());
				}
				return '--';
			},
			//DE1300289 default order for inactive accounts
			sortFunction: (rowA, rowB) => {
				const dateA = rowA.updateDateRaw ? rowA.updateDateRaw.getTime() : 0;
				const dateB = rowB.updateDateRaw ? rowB.updateDateRaw.getTime() : 0;
				return dateA - dateB;
			},
			maxWidth: "140px",
		},
		{
			name: 'Status',
			selector: row => row.status,
			sortable: true,
			maxWidth: "50px",
		},	
		{
			cell: (row) => {
				return (<SupplierUserPopUp key={row.emailAddress} editMode="Edit" isSPUAView={false} btnClass="sEditBtn" currentUser={currentUser} currentContact={row} onUserAction={onUserUpdate} currentCompanyId={row.supplierId} spuaEmail="" isAdminDisplay={true} />);
			},
			right: "true",
			maxWidth: "50px",

		}
	];

	//Custom styles for the table
	const customStyles = {
		table: {
			style: {
				padding: "0",
				margin: "0",
			},
		},
		responsiveWrapper: {
			style: {
				borderRadius: "unset",
			},
		},
		headCells: {
			style: {
				fontSize: "14px",
				justifyContent: "left !important",
				color: "white",
				paddingLeft: "1%",
				borderRight: "1px solid white",
				fontWeight: "400",
				wrap: true,
				whiteSpace: "normal",
				wordWrap: "break-word", 
			},
		},
		head: {
			style: {
				zIndex: "99",
			},
		},
		headRow: {
			style: {
				backgroundColor: "#003C71",
			},
		},
	};
	return (data.length > 0) ? <DataTable columns={columns} data={data} pagination striped responsive customStyles={customStyles} paginationPerPage={25} defaultSortFieldId="updateDate"
		defaultSortAsc={false} onSort={onSort} /> : <div>No results found</div>;
};

